/**
 * SkillLock-Tasks im Kundenkontext-Panel (Spec 10 / 7.2).
 *
 * Der Status kommt als **Text** aus SkillLock (`status_name`) und wird als
 * Text angezeigt — es gibt kein Enum, das man uebersetzen koennte. Entschieden
 * wird nur an `status_is_done`: erledigt oder nicht.
 */
import type { SkilllockTaskLink } from '@tickethub/shared';
import { CheckCircle2, CircleDashed, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type SkilllockPanelProps = {
  tasks: SkilllockTaskLink[];
  /** `false` = fuer diesen Mandanten nicht eingerichtet — Panel bleibt leer. */
  enabled: boolean;
  disabled: boolean;
  onCreate: (title: string) => Promise<void>;
};

export function SkilllockPanel({ tasks, enabled, disabled, onCreate }: SkilllockPanelProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  if (!enabled) return null;

  async function create() {
    setBusy(true);
    try {
      await onCreate(title.trim());
      setTitle('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label={t('skilllock.tasks_title')}>
      <h2 className="text-sm font-medium">{t('skilllock.tasks_title')}</h2>

      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('skilllock.tasks_empty')}</p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <li key={task.id} className="space-y-1 text-sm">
              <div className="flex items-start gap-2">
                {task.status_is_done ? (
                  <CheckCircle2 aria-hidden="true" className="mt-0.5 size-4 text-green-600" />
                ) : (
                  <CircleDashed aria-hidden="true" className="mt-0.5 size-4 text-muted-foreground" />
                )}
                <span className={task.deleted ? 'line-through' : undefined}>
                  {task.url ? (
                    <a className="hover:underline" href={task.url} target="_blank" rel="noreferrer">
                      {task.title}
                    </a>
                  ) : (
                    task.title
                  )}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 pl-6">
                {/* Frei benannte Kanban-Spalte — unuebersetzt, so wie sie kommt. */}
                <Badge variant={task.status_is_done ? 'default' : 'outline'}>
                  {task.status_name ?? t('skilllock.status_unknown')}
                </Badge>
                {task.deleted ? (
                  <Badge variant="destructive">{t('skilllock.deleted')}</Badge>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!disabled ? (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <Input
            value={title}
            placeholder={t('skilllock.new_task_placeholder')}
            onChange={(event) => setTitle(event.target.value)}
          />
          <Button type="submit" size="sm" variant="outline" disabled={busy}>
            <Plus aria-hidden="true" />
            {t('skilllock.create_task')}
          </Button>
        </form>
      ) : null}
    </section>
  );
}
